import { useState, useEffect, SyntheticEvent, useMemo } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import SearchOutlined from '@mui/icons-material/SearchOutlined';
import axios from 'axios';
import { DataTable, DateField, StatusChip, type DataTableColumn } from '@routes-plan/ui';
import { Office } from '../../models/office';
import { Route } from '../../models/route';
import { LoanReport } from '../../models/loan-report';
import moment from "moment-timezone";
import { minYmd, nowOperationalDateString } from '../../utils/operationalDateTime';
import { formatCurrency, formatNumber } from '../../utils/format/currency';
import OfficeExpenseModal from './modals/OfficeExpenseModal';
import LoanRefinancingModal from './modals/LoanRefinancingModal';
import { useT } from '../../i18n';
import { setPageTitle } from '../../utils/pageTitle';
import { OfficeSelect, ReportEmpty, ReportFilters, ReportPage, ReportResults, RouteMultiSelect } from './components/ReportLayout';

/** Fila del resumen: un día de una ruta, o las filas de Total y Promedio al final. */
type SummaryRow = Record<string, any> & { id: string | number };

interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

const DailySummary: React.FC<ShowAlerts> = ({ showAlert, showLoading, hideLoading }) => {
  const t = useT();
  setPageTitle('menu.reports.daily-summary');
  const current_date = nowOperationalDateString();
  const [queried, setQueried] = useState(false);

  const [office_id, setOffice_id] = useState('');
  const [collectsOffice, setCollectsOffice] = useState<Route[]>([]);
  const [selectedCollects, setSelectedCollects] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [start_date, setStart_date] = useState(current_date);
  const [end_date, setEnd_date] = useState(current_date);
  const [datas, setDatas] = useState<any[]>([]);
  const [offices, setOffices] = useState<Office[]>([]);
  const [openModal, setOpenModal] = useState(false);
  const [openModalRefi, setOpenModalRefi] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any | null>(null);
  const [modalData, setModalData] = useState<{ cashBoxId: number; date: string; dateISO?: string; routeName: string; routeId?: number | string; typeOperation: number; isOutflow?: boolean } | null>(null);
  const [modalLoan, setModalLoan] = useState<{
    cashBoxId: number;
    startDate: string;
    endDate: string;
    routeName: string;
    routeId: string;
  } | null>(null);

  useEffect(() => {
    axios.get('offices').then(({ data }) => setOffices(data.data));
  }, []);

  useEffect(() => {
    if (office_id) {
      axios.get(`offices/${office_id}/routes`).then(({ data }) => {
        setCollectsOffice(data);
        setSelectedCollects([]);
        setSelectAll(false);
      });
    }
  }, [office_id]);

  const handleOpenModal = (row: any) => {
    setSelectedRow(row);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setModalData(null);
    setSelectedRow(null);
  };
  const handleCloseModalLoan = () => {
    setOpenModalRefi(false);
    setModalLoan(null);
    setSelectedRow(null);
  };
  const handleOpenModalWithType = (row: any, typeOperation: number) => {
    // API ModalExpense: type 1 + out true incluye traslados a caja cobro y gastos oficina (origen=caja, destino null).
    // Enviar typeOperation 1 para "Gastos Oficina Suma", no 2 (2 + isOutflow usa solo la caja destino).
    setModalData({
      cashBoxId: row.cashBoxId,
      date: row.date,
      dateISO: row.dateISO,
      routeName: row.route,
      routeId: row.routeId,
      typeOperation: typeOperation === 1 ? 1 : typeOperation === 2 ? 2 : 3,
      isOutflow: typeOperation === 1,
    });
    setOpenModal(true);
  };
  const handleOpenModalLoanType = (row: any) => {
    setModalLoan({
      cashBoxId: row.cashBoxId,
      startDate: start_date,
      endDate: end_date,
      routeName: row.route,
      routeId: String(row.routeId),
    });
    setOpenModalRefi(true);
  };
  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();
    if (!selectedCollects || selectedCollects.length === 0) {
      showAlert(t('summaryReports.selectAtLeastOneRoute'), 'warning');
      return;
    }
    const formData = { endDate: end_date, startDate: start_date, routeId: selectedCollects };
    showLoading();
    setQueried(true);
    try {
      const response = await axios.post('/reports/daily-summary', formData);
      setDatas(response.data.data);
      await new Promise((resolve) => setTimeout(resolve, 1000));

      if (!response.data.data || response.data.data.length === 0) {
        showAlert(t('summaryReports.noRecordsForSelection'), 'info');
        return;
      }

    } catch (error: any) {
      console.error('Error:', error);
      if (error?.response?.status === 403) {
        showAlert(t('summaryReports.daily.noPermission'), 'error');
        return;
      }
      showAlert(t('summaryReports.daily.loadError'), 'error');
    } finally {
      hideLoading();
    }
  };

  const numericColumns = [
    'paymentTotal', 'loanTotal', 'interestTotal', 'refinanceValue',
    'insuranceChargeTotal',
    'refinanceTotal', 'cashBoxAdjustmentTotal', 'otherIncomeTotal', 'otherExpenseTotal',
    'expenseTotal', 'cash', 'base', 'cashBox', 'cashDifference',
  ];

  const isSummaryRow = (row: SummaryRow) => row.id === 'total-row' || row.id === 'average-row';

  const rowNumeric = (row: SummaryRow, key: string) => {
    const v = row[key];
    return typeof v === 'number' && Number.isFinite(v) ? v : Number(v) || 0;
  };

  const isDataRowWithoutMovements = (row: SummaryRow) =>
    !isSummaryRow(row) &&
    rowNumeric(row, 'paymentTotal') === 0 &&
    rowNumeric(row, 'loanTotal') === 0 &&
    rowNumeric(row, 'insuranceChargeTotal') === 0 &&
    rowNumeric(row, 'refinanceValue') === 0 &&
    rowNumeric(row, 'refinanceTotal') === 0 &&
    rowNumeric(row, 'otherIncomeTotal') === 0 &&
    rowNumeric(row, 'otherExpenseTotal') === 0 &&
    rowNumeric(row, 'cashBoxAdjustmentTotal') === 0 &&
    rowNumeric(row, 'expenseTotal') === 0;

  /** Las filas de Total y Promedio ya traen el valor formateado; las de cada día, el número. */
  const moneyText = (row: SummaryRow, field: string) =>
    isSummaryRow(row) ? String(row[field] ?? '-') : formatNumber(rowNumeric(row, field));

  const strong = (row: SummaryRow, node: React.ReactNode) =>
    isSummaryRow(row) ? <Box component="strong" sx={{ fontWeight: 700 }}>{node}</Box> : node;

  const moneyColumn = (
    field: string,
    header: string,
    detail?: { typeOperation?: number; onClick?: (row: SummaryRow) => void },
  ): DataTableColumn<SummaryRow> => ({
    id: field,
    header,
    align: 'right',
    value: (row) => moneyText(row, field),
    render: (row) => {
      const text = strong(row, moneyText(row, field));
      if (!detail || isSummaryRow(row)) return text;
      const open = () => {
        if (detail.onClick) {
          detail.onClick(row);
          return;
        }
        if (typeof detail.typeOperation === 'number') {
          handleOpenModalWithType(row, detail.typeOperation);
          return;
        }
        handleOpenModal(row);
      };
      return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
          {text}
          <Tooltip title={t('summaryReports.daily.viewDetail')}>
            <IconButton size="small" color="primary" aria-label={`${t('summaryReports.daily.viewDetail')}: ${header}`} onClick={open} sx={{ p: 0.25 }}>
              <SearchOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      );
    },
  });

  const tableColumns = useMemo<DataTableColumn<SummaryRow>[]>(() => [
    {
      id: 'route',
      header: t('summaryReports.shared.route'),
      value: (row) => row.route,
      render: (row) => strong(row, row.route),
    },
    {
      id: 'date',
      header: t('common.date'),
      value: (row) => row.date ?? '',
      render: (row) =>
        isDataRowWithoutMovements(row) ? (
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
            {row.date}
            <StatusChip label={t('summaryReports.daily.noMovement')} tone="danger" />
          </Box>
        ) : (
          row.date ?? ''
        ),
    },
    moneyColumn('paymentTotal', t('summaryReports.col.payments')),
    moneyColumn('loanTotal', t('summaryReports.col.loans')),
    moneyColumn('interestTotal', t('summaryReports.col.utility')),
    moneyColumn('otherIncomeTotal', t('summaryReports.col.officeExpensesPlus'), { typeOperation: 1 }),
    moneyColumn('otherExpenseTotal', t('summaryReports.col.officeExpensesMinus'), { typeOperation: 2 }),
    moneyColumn('expenseTotal', t('summaryReports.col.routeCashExpenses'), { typeOperation: 3 }),
    moneyColumn('cash', t('summaryReports.col.cash')),
    moneyColumn('base', t('summaryReports.col.base')),
    moneyColumn('cashBox', t('summaryReports.col.cashTotal')),
    moneyColumn('insuranceChargeTotal', t('summaryReports.col.managementFees')),
    moneyColumn('cashDifference', t('summaryReports.col.cashDifference')),
    {
      id: 'payments',
      header: t('summaryReports.col.paysCount'),
      align: 'right',
      value: (row) => row.payments ?? '',
    },
    {
      id: 'paymentPercentage',
      header: t('summaryReports.col.payPercentage'),
      align: 'right',
      value: (row) => (isSummaryRow(row) ? String(row.paymentPercentage ?? '-') : `${rowNumeric(row, 'paymentPercentage').toFixed(2)}%`),
      render: (row) => strong(row, isSummaryRow(row) ? String(row.paymentPercentage ?? '-') : `${rowNumeric(row, 'paymentPercentage').toFixed(2)}%`),
    },
    moneyColumn('refinanceTotal', t('summaryReports.col.refinanceUtility')),
    moneyColumn('refinanceValue', t('summaryReports.col.refinanceValue'), { onClick: handleOpenModalLoanType }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t, start_date, end_date]);

  const buildRows = (groupData: LoanReport[]) => {
    const sortedRows = [...groupData].sort((a, b) => {
      const da = typeof a.date === 'string' ? a.date : moment(a.date).format('YYYY-MM-DD');
      const db = typeof b.date === 'string' ? b.date : moment(b.date).format('YYYY-MM-DD');
      const c = String(da).localeCompare(String(db));
      if (c !== 0) return c;
      return Number(a.id) - Number(b.id);
    });

    const dataRows: SummaryRow[] = sortedRows.map((r) => {
      const rawBox = r.cashBox as unknown;
      const boxIdentifier =
        (rawBox && typeof rawBox === 'object' && 'id' in rawBox
          ? Number((rawBox as { id?: number }).id)
          : undefined) ?? (r as unknown as { cashBoxId?: number }).cashBoxId ?? 0;
      return {
        id: r.id,
        route: r.route.name,
        routeId: r.route.id,
        cashBoxId: boxIdentifier,
        date: r.date ? moment(r.date).format('YYYY/MM/DD') : '',
        dateISO: r.date ? moment(r.date).format('YYYY-MM-DD') : '',
        hour: r.date ? moment(r.date).format('HH:mm:ss') : '',
        payments: t('summaryReports.daily.payCount', { paid: r.paymentCount, total: r.clientCount }),
        paymentPercentage: r.clientCount > 0 ? (r.paymentCount / r.clientCount) * 100 : 0,
        paymentTotal: r.paymentTotal,
        loanTotal: r.loanTotal,
        interestTotal: r.interestTotal,
        insuranceChargeTotal: r.insuranceChargeTotal,
        refinanceValue: r.refinanceValue,
        refinanceTotal: r.refinanceTotal,
        otherIncomeTotal: r.otherIncomeTotal,
        otherExpenseTotal: r.otherExpenseTotal,
        cashBoxAdjustmentTotal: r.totalCashBoxAdjustments ?? 0,
        expenseTotal: r.expenseTotal,
        cash: r.cash,
        base: r.base,
        cashBox: r.cashBoxValue,
        cashDifference: r.cashDifference,
      };
    });

    // Promedio: solo sobre los días con movimiento (igual que antes).
    const rowsWithOperation = dataRows.filter((row) => !isDataRowWithoutMovements(row));
    const operationDayCount = rowsWithOperation.length;
    const totalValues: Record<string, number> = {};
    const averageValues: Record<string, number> = {};
    numericColumns.forEach((col) => {
      const sum = dataRows.reduce((acc, row) => acc + rowNumeric(row, col), 0);
      totalValues[col] = sum;
      if (operationDayCount === 0) {
        averageValues[col] = 0;
      } else {
        const opSum = rowsWithOperation.reduce((acc, row) => acc + rowNumeric(row, col), 0);
        averageValues[col] = opSum / operationDayCount;
      }
    });

    const tableRows: SummaryRow[] = [
      ...dataRows,
      {
        id: 'total-row',
        route: t('summaryReports.total'),
        ...Object.fromEntries(numericColumns.map((col) => [col, formatCurrency(totalValues[col])])),
      },
      {
        id: 'average-row',
        route: t('summaryReports.average'),
        ...Object.fromEntries(numericColumns.map((col) => [col, formatCurrency(averageValues[col])])),
      },
    ];
    return { sortedRows, tableRows };
  };

  return (
    <ReportPage title={t('summaryReports.daily.title')} description={t('summaryReports.daily.description')}>
      <ReportFilters onSubmit={() => submit({ preventDefault: () => undefined } as SyntheticEvent)}>
        <DateField
          label={t('summaryReports.startDate')}
          value={start_date}
          onChange={(v) => setStart_date(v ?? '')}
          max={minYmd(end_date, nowOperationalDateString())}
          size="small"
          fullWidth
        />
        <DateField
          label={t('summaryReports.endDate')}
          value={end_date}
          onChange={(v) => setEnd_date(v ?? '')}
          min={start_date}
          max={nowOperationalDateString()}
          size="small"
          fullWidth
        />
        <OfficeSelect offices={offices} value={office_id} onChange={setOffice_id} />
        <RouteMultiSelect
          routes={collectsOffice}
          value={selectedCollects}
          onChange={(ids) => {
            setSelectedCollects(ids);
            setSelectAll(ids.length > 0 && ids.length === collectsOffice.length);
          }}
        />
      </ReportFilters>

      {datas.length > 0 ? (
        datas.map((grupo: any) => {
          const { sortedRows, tableRows } = buildRows(grupo.data ?? []);
          return (
            <ReportResults
              key={grupo.routeId}
              fill={datas.length === 1}
              title={grupo.routeName}
              meta={t('summaryReports.dateRange', { start: start_date, end: end_date })}
            >
              <Box
                sx={
                  datas.length === 1
                    ? {
                        flex: '1 1 0',
                        minHeight: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        '& > div': { flex: '1 1 0', minHeight: 0, display: 'flex', flexDirection: 'column' },
                        '& .MuiTableContainer-root': { flex: '1 1 0', minHeight: 160 },
                      }
                    : undefined
                }
              >
                <DataTable<SummaryRow>
                  rows={tableRows}
                  columns={tableColumns}
                  getRowId={(row) => row.id}
                  searchable={false}
                  initialPageSize={100}
                  pageSizeOptions={[50, 100, 500]}
                  maxHeight={datas.length === 1 ? 'none' : undefined}
                  exportFileName={t('summaryReports.daily.exportFile', { route: sortedRows[0]?.route?.name ?? t('summaryReports.daily.exportGroup') })}
                  ariaLabel={`${t('summaryReports.daily.title')}: ${grupo.routeName}`}
                />
              </Box>
            </ReportResults>
          );
        })
      ) : (
        <ReportResults>
          <ReportEmpty queried={queried} />
        </ReportResults>
      )}

      <OfficeExpenseModal
        open={openModal}
        onClose={handleCloseModal}
        clientData={modalData}
        showAlert={showAlert}
        showLoading={showLoading}
        hideLoading={hideLoading}
      />
      <LoanRefinancingModal
        open={openModalRefi}
        onClose={handleCloseModalLoan}
        clientLoan={modalLoan}
        showAlert={showAlert}
        showLoading={showLoading}
        hideLoading={hideLoading}
      />
    </ReportPage>
  );
};

export default DailySummary;
